import React, {useEffect, useRef, useState} from 'react';
import {Link as RouterLink} from 'react-router';
import {
    Alert,
    Avatar,
    Box,
    Button,
    Chip,
    Divider,
    IconButton,
    InputAdornment,
    Link,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import NoteAddOutlinedIcon from '@mui/icons-material/NoteAddOutlined';
import Timestamp from '../../../components/Timestamp';
import UserAvatar from '../../../components/UserAvatar';
import {useSession} from '../../../firebase/UserProvider';
import TabLoading from './TabLoading';

function authorLabel(author) {
    if (!author) return 'Unknown';
    const fromParts = [author.firstName, author.lastName].filter(Boolean).join(' ').trim();
    return fromParts || author.displayName || author.userId || 'Unknown';
}

function authorRoles(author) {
    if (!author) return [];
    const fromList = Array.isArray(author.roles) ? author.roles : [];
    const merged = [...fromList, author.role].filter(Boolean).map((r) => String(r).trim()).filter(Boolean);
    const seen = new Set();
    const out = [];
    for (const role of merged) {
        const key = role.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(role);
    }
    return out;
}

function isAnalystAuthor(author) {
    return authorRoles(author).some((r) => r.toLowerCase() === 'analyst');
}

function authorExpertHref(author) {
    const handle = author?.userHandle || author?.handle;
    if (!handle || !isAnalystAuthor(author)) return null;
    return `/experts/${encodeURIComponent(handle)}`;
}

function formatRoleLabel(role) {
    return String(role)
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Embeddable notes composer + feed for KB entities.
 *
 * @param {Object} props
 * @param {Array} props.notes
 * @param {boolean} props.loading
 * @param {Error|null} [props.error]
 * @param {boolean} [props.canPost]
 * @param {(body: string) => Promise<unknown>} [props.onPost]
 * @param {(noteId: string, body: string) => Promise<unknown>} [props.onUpdate]
 * @param {(noteId: string) => Promise<unknown>} [props.onDelete]
 * @param {boolean} [props.saving]
 * @param {Error|null} [props.postError]
 * @param {boolean} [props.embedded] — compact layout for Summary tab embedding
 */
const EntityNotesPanel = ({
    notes,
    loading,
    error = null,
    canPost = false,
    onPost,
    onUpdate,
    onDelete,
    saving = false,
    postError = null,
    embedded = false,
}) => {
    const {user} = useSession() || {};
    const currentUserId = user?.uid || user?.id || null;
    const [draft, setDraft] = useState('');
    const [localError, setLocalError] = useState(null);
    const [editingNoteId, setEditingNoteId] = useState(null);
    const [editDraft, setEditDraft] = useState('');
    const listEndRef = useRef(null);
    const prevCountRef = useRef(0);

    useEffect(() => {
        if (!notes?.length) {
            prevCountRef.current = 0;
            return;
        }
        if (notes.length >= prevCountRef.current) {
            listEndRef.current?.scrollIntoView?.({behavior: 'smooth', block: 'nearest'});
        }
        prevCountRef.current = notes.length;
    }, [notes]);

    useEffect(() => {
        if (!editingNoteId) return;
        if (!notes?.some((note) => note.id === editingNoteId)) {
            setEditingNoteId(null);
            setEditDraft('');
        }
    }, [notes, editingNoteId]);

    if (loading) return <TabLoading/>;

    const handlePost = async () => {
        const body = draft.trim();
        if (!body || !onPost) return;
        setLocalError(null);
        try {
            await onPost(body);
            setDraft('');
        } catch (err) {
            setLocalError(err);
        }
    };

    const handleEditStart = (note) => {
        setLocalError(null);
        setEditingNoteId(note.id);
        setEditDraft(note.body || '');
    };

    const handleEditCancel = () => {
        setEditingNoteId(null);
        setEditDraft('');
    };

    const handleEditSave = async () => {
        const body = editDraft.trim();
        if (!editingNoteId || !body || !onUpdate) return;
        setLocalError(null);
        try {
            await onUpdate(editingNoteId, body);
            setEditingNoteId(null);
            setEditDraft('');
        } catch (err) {
            setLocalError(err);
        }
    };

    const handleDelete = async (noteId) => {
        if (!noteId || !onDelete) return;
        setLocalError(null);
        try {
            await onDelete(noteId);
            if (editingNoteId === noteId) {
                setEditingNoteId(null);
                setEditDraft('');
            }
        } catch (err) {
            setLocalError(err);
        }
    };

    const surfaceError = localError || postError || error;
    const feedMaxHeight = embedded ? 320 : 420;
    const MAX_CHARS = 2000;
    const hasNotes = Boolean(notes?.length);

    return (
        <Stack sx={embedded ? undefined : {minHeight: 280}} id="entity-notes">
            {embedded ? (
                <Box px={2} pt={1}>
                    <Divider sx={{mb: 1.5}}/>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{mb: hasNotes ? 1.5 : 0}}>
                        <NoteAddOutlinedIcon sx={{fontSize: 15, color: 'text.secondary'}}/>
                        <Typography variant="subtitle2" fontWeight={700} color="text.secondary">Notes</Typography>
                    </Stack>
                </Box>
            ) : null}

            {/* ── Feed — only rendered when there are notes ── */}
            {hasNotes || !embedded ? (
                <Box sx={{flex: 1, minHeight: 0, maxHeight: feedMaxHeight, overflowY: 'auto', px: 2, pt: embedded ? 0 : 2}}>
                    {surfaceError ? (
                        <Alert severity="error" sx={{mb: 2}}>
                            {surfaceError.message || 'Failed to load notes.'}
                        </Alert>
                    ) : null}

                    {!hasNotes ? (
                        <Typography variant="caption" color="text.disabled" sx={{display: 'block', py: 0.5}}>
                            Be the first to leave a note on this entity.
                        </Typography>
                    ) : (
                    <Stack spacing={0} divider={<Divider/>}>
                        {notes.map((note) => {
                            const author = note.author || {};
                            const name = authorLabel(author);
                            const expertHref = authorExpertHref(author);
                            const roles = authorRoles(author);
                            const isOwn = Boolean(currentUserId && author.userId === currentUserId);
                            const isEditing = editingNoteId === note.id;
                            const avatarUser = {
                                ...author,
                                profilePicture: author.avatarUrl || author.profilePicture,
                                hasAvatar: Boolean(author.avatarUrl || author.hasAvatar),
                            };
                            return (
                                <Box key={note.id} sx={{py: 1.5}}>
                                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                                        <UserAvatar
                                            user={avatarUser}
                                            userId={author.userId}
                                            sx={{width: 32, height: 32, fontSize: 13, mt: 0.25}}
                                        />
                                        <Stack spacing={0.25} minWidth={0} flex={1}>
                                            <Stack
                                                direction="row"
                                                spacing={1}
                                                alignItems="center"
                                                justifyContent="space-between"
                                                flexWrap="wrap"
                                                useFlexGap
                                            >
                                                <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap minWidth={0}>
                                                    {expertHref ? (
                                                        <Link
                                                            component={RouterLink}
                                                            to={expertHref}
                                                            variant="body2"
                                                            fontWeight={600}
                                                            underline="hover"
                                                            color="text.primary"
                                                        >
                                                            {name}
                                                        </Link>
                                                    ) : (
                                                        <Typography variant="body2" fontWeight={600}>{name}</Typography>
                                                    )}
                                                    {roles.map((role) => (
                                                        <Chip
                                                            key={role}
                                                            label={formatRoleLabel(role)}
                                                            size="small"
                                                            variant="outlined"
                                                            sx={{height: 18, '& .MuiChip-label': {px: 0.6, fontSize: '0.65rem'}}}
                                                        />
                                                    ))}
                                                    {note.createdAt ? (
                                                        <Timestamp
                                                            component={Typography}
                                                            variant="caption"
                                                            color="text.disabled"
                                                            value={note.createdAt}
                                                        />
                                                    ) : null}
                                                    {note.updatedAt ? (
                                                        <Typography component="span" variant="caption" color="text.disabled" sx={{fontStyle: 'italic'}}>
                                                            (edited)
                                                        </Typography>
                                                    ) : null}
                                                </Stack>
                                                {isOwn && !isEditing && (onUpdate || onDelete) ? (
                                                    <Stack direction="row" spacing={0} sx={{opacity: 0.5, '&:hover': {opacity: 1}}}>
                                                        {onUpdate ? (
                                                            <Tooltip title="Edit note">
                                                                <IconButton size="small" aria-label="edit note" onClick={() => handleEditStart(note)} disabled={saving}>
                                                                    <EditIcon sx={{fontSize: 14}}/>
                                                                </IconButton>
                                                            </Tooltip>
                                                        ) : null}
                                                        {onDelete ? (
                                                            <Tooltip title="Delete note">
                                                                <IconButton size="small" aria-label="delete note" onClick={() => handleDelete(note.id)} disabled={saving} color="error">
                                                                    <DeleteOutlineIcon sx={{fontSize: 14}}/>
                                                                </IconButton>
                                                            </Tooltip>
                                                        ) : null}
                                                    </Stack>
                                                ) : null}
                                            </Stack>
                                            {isEditing ? (
                                                <Stack spacing={1} sx={{mt: 0.5}}>
                                                    <TextField
                                                        multiline
                                                        minRows={2}
                                                        maxRows={6}
                                                        fullWidth
                                                        size="small"
                                                        value={editDraft}
                                                        onChange={(e) => setEditDraft(e.target.value)}
                                                        disabled={saving}
                                                        inputProps={{'aria-label': 'Edit note text'}}
                                                    />
                                                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                                                        <Button size="small" onClick={handleEditCancel} disabled={saving}>Cancel</Button>
                                                        <Button
                                                            variant="contained"
                                                            size="small"
                                                            disabled={saving || !editDraft.trim()}
                                                            onClick={handleEditSave}
                                                        >
                                                            {saving ? 'Saving…' : 'Save'}
                                                        </Button>
                                                    </Stack>
                                                </Stack>
                                            ) : (
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{mt: 0.25, whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.6}}
                                                >
                                                    {note.body}
                                                </Typography>
                                            )}
                                        </Stack>
                                    </Stack>
                                </Box>
                            );
                        })}
                        <div ref={listEndRef}/>
                    </Stack>
                )}
            </Box>
            ) : null}

            {/* ── Composer ── */}
            <Box sx={{px: 2, py: 1.5, borderTop: 1, borderColor: 'divider', bgcolor: (t) => t.palette.action.hover}}>
                {canPost ? (
                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                        {user ? (
                            <Avatar
                                src={user.photoURL || user.avatarUrl || undefined}
                                alt={user.displayName || ''}
                                sx={{width: 32, height: 32, fontSize: 13, mt: 0.5, bgcolor: 'primary.main'}}
                            >
                                {(user.displayName || user.email || '?')[0].toUpperCase()}
                            </Avatar>
                        ) : null}
                        <Stack flex={1} spacing={0.5}>
                            <TextField
                                multiline
                                minRows={1}
                                maxRows={5}
                                fullWidth
                                size="small"
                                placeholder="Add a note… (Cmd+Enter to post)"
                                value={draft}
                                onChange={(e) => {
                                    if (e.target.value.length <= MAX_CHARS) setDraft(e.target.value);
                                }}
                                onKeyDown={(e) => {
                                    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                                        e.preventDefault();
                                        if (draft.trim() && !saving && !editingNoteId) handlePost();
                                    }
                                }}
                                disabled={saving || Boolean(editingNoteId)}
                                inputProps={{'aria-label': 'Note text'}}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        bgcolor: 'background.paper',
                                    },
                                }}
                                InputProps={{
                                    endAdornment: draft.length > MAX_CHARS * 0.8 ? (
                                        <InputAdornment position="end">
                                            <Typography
                                                variant="caption"
                                                color={draft.length >= MAX_CHARS ? 'error' : 'text.disabled'}
                                            >
                                                {MAX_CHARS - draft.length}
                                            </Typography>
                                        </InputAdornment>
                                    ) : null,
                                }}
                            />
                            <Stack direction="row" justifyContent="flex-end">
                                <Button
                                    variant="contained"
                                    size="small"
                                    disabled={saving || Boolean(editingNoteId) || !draft.trim()}
                                    onClick={handlePost}
                                    sx={{minWidth: 72}}
                                >
                                    {saving ? 'Posting…' : 'Post'}
                                </Button>
                            </Stack>
                        </Stack>
                    </Stack>
                ) : (
                    <Typography variant="body2" color="text.secondary">Sign in to post a note.</Typography>
                )}
            </Box>
        </Stack>
    );
};

export default EntityNotesPanel;
